<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { getContext } from 'svelte';
  import OperationCompleteModal from '../etc/modal/OperationCompleteModal.svelte';
  import Businesses from '../constants/Business';
  import EnecomUtil from '../util/EnecomUtil';
  import { saveJwt } from '../components/auth/TemporaryLoginUser';
  import { isValidDate } from '../components/validator/Validator';
  import RepositoryFactory, { AUTHENICATE } from '../repositories/RepositoryFactory';
  import { format } from 'date-fns';
  import { ja } from 'date-fns/locale';
  import { form, field } from 'svelte-forms';
  import { required } from 'svelte-forms/validators';

  const isEnecom: boolean = EnecomUtil.isEnecom();
  const { open, close }: any = getContext('simple-modal');

  const businessItems: any = Businesses.LOGIN_BUSINESSES;
  const contactUrlList = [
    { code: 'HIKARI_COLLABO', name: 'https://bb.unext.co.jp/contact' },
    { code: 'HIKARI_01', name: 'https://bb01.unext.co.jp/contact' }
  ];

  // input項目
  const businessCode = field('businessCode', isEnecom ? 'UMOBILE_D_ENECOM' : 'UMOBILE_D', [required()]);
  const mailAddress = field('mailAddress', '', [required()]);
  const birthDate = field('birthDate', '', [required(), isValidDate]);
  const telNo = field('telNo', '', [required()]);
  const myForm = form(businessCode, mailAddress, birthDate, telNo);

  let isConfirmNotAuthorized: boolean = false;
  let isConfirmNotUnique: boolean = false;

  // label
  let labelTel: '電話番号' | 'SIM電話番号' = 'SIM電話番号';

  $: isSimPhoneNumber = ['UMOBILE_D', 'UMOBILE_D_ENECOM', 'UMOBILE_EA', 'UMOBILE_SONET', 'UMOBILE_PREMIUM'].includes(
    $businessCode.value
  );
  $: labelTel = isSimPhoneNumber ? 'SIM電話番号' : '電話番号';

  const handleTemporaryLogin = async () => {
    isConfirmNotAuthorized = false;
    isConfirmNotUnique = false;

    let isFormValid = false;
    await myForm.validate().then(() => {
      isFormValid = $myForm.valid;
    });
    if (!isFormValid) {
      return;
    }

    const authenicateRepository = RepositoryFactory[AUTHENICATE];
    authenicateRepository
      .temporaryLoginByResetLoginInfo({
        businessCode: $businessCode.value,
        mailAddress: $mailAddress.value,
        birthDate: $birthDate.value,
        telNo: $telNo.value
      })
      .then((response) => {
        if (response) {
          saveJwt(response.token);
          showSuccessedModal();
        }
      })
      .catch((error) => {
        // 認証エラーを判定
        switch (error.response?.status) {
          case 401:
          case 403:
          case 404:
            isConfirmNotAuthorized = true;
            break;
          case 409:
            // NOTE 該当レコードが複数件存在する（光01、光コラボでのみ検索条件「メール、SIM電話番号、生年月日」では一意のレコードを導出できないケースがある）
            // この場合はカスタマーセンターへの問い合わせに誘導する
            isConfirmNotUnique = true;
            break;
        }
        // TODO:認証エラー以外の場合の処理
      });
  };

  const showSuccessedModal = () => {
    open(
      OperationCompleteModal,
      {
        modalTitle: 'パスワード設定用メール を送信しました',
        modalMessage: 'ご登録のメールアドレスに パスワード設定用メール を送信しました',
        handleCloseEvent: () => {
          push(`/login`);
          close();
        }
      },
      {
        closeButton: false,
        closeOnEsc: false,
        closeOnOuterClick: false
      }
    );
  };

  const getContactUrl = (): string => {
    return contactUrlList.find((v) => v.code === $businessCode.value).name;
  };
</script>

<!-- 本体 -->
<div class="md:h-screen flex flex-col justify-between items-center login-background">
  <header class="container top-0 z-0">
    <div class="justify-between items-center" style="height:56px">
      {#if isEnecom}
        <img style="height:36px" class="object-none w-full" src="/image/enecom_logo.png" alt="logo" />
      {:else}
        <img style="height:36px" class="object-none w-full" src="/image/unext_communication_logo.svg" alt="logo" />
      {/if}
      <hr class="items-center" style="border: 1px solid rgba(0,10,23,0.2)" />
    </div>
  </header>

  <main class="container mt-20 mb-auto">
    <div class="grid place-items-center text-3xl">パスワード確認・変更</div>

    {#if isConfirmNotAuthorized}
      <div class="grid place-items-center mt-10">
        <div class="content-center shadow-sm alert-background">
          <div class="bg-red-100 border-red-500 text-red-700 px-4 py-3" role="alert">
            <p class="font-bold">ログインIDが確認できませんでした</p>
          </div>
        </div>
      </div>
    {/if}
    {#if isConfirmNotUnique}
      <div class="grid place-items-center mt-10">
        <div class="content-center shadow-sm alert-background">
          <div class="bg-red-100 border-red-500 text-red-700 px-4 py-3" role="alert">
            <p class="font-bold">申し訳ございません。カスタマーセンターにお問い合わせください。</p>
            <p class="font-bold">
              <a href={getContactUrl()} rel="noopener noreferrer" target="_blank">{getContactUrl()}</a>
            </p>
          </div>
        </div>
      </div>
    {/if}
    <div class="grid place-items-center mt-10">
      <div class="content-center shadow-sm p-5 form-background">
        <form>
          <div class="grid place-items-center">
            <div class="form-container">
              {#if !isEnecom}
                <div class="">
                  <label for="businessCode" class="block form-input-label">サービス</label>
                  <select
                    id="businessCode"
                    name="businessCode"
                    class="block p-3 mt-1 appearance"
                    bind:value={$businessCode.value}
                    required
                  >
                    {#each businessItems as { code, name }}
                      <option value={code}>{name}</option>
                    {/each}
                  </select>
                  {#if $myForm.hasError('businessCode.required')}
                    <p class="text-red-500 text-xs">サービスを選択してください</p>
                  {/if}
                </div>
              {/if}
              <div class="">
                <label for="mailAddress" class="block mt-4 form-input-label">メールアドレス</label>
                <input
                  type="email"
                  id="mailAddress"
                  name="mailAddress"
                  autocomplete="off"
                  class="block p-3 mt-1 appearance-none"
                  bind:value={$mailAddress.value}
                  required
                />
                {#if $myForm.hasError('mailAddress.required')}
                  <p class="text-red-500 text-xs mt-2">メールアドレスを入力してください</p>
                {/if}
              </div>

              <div class="">
                <label for="birthDate" class="block mt-4 form-input-label">生年月日</label>
                <input
                  type="date"
                  id="birthDate"
                  name="birthDate"
                  autocomplete="off"
                  class="block p-3 mt-1 appearance-none"
                  bind:value={$birthDate.value}
                  max={format(new Date(), 'yyyy-MM-dd', { locale: ja })}
                  required
                />
                {#if $myForm.hasError('birthDate.required')}
                  <p class="text-red-500 text-xs mt-2">生年月日を入力してください</p>
                {/if}
                {#if $myForm.hasError('birthDate.invalid_date')}
                  <p class="text-red-500 text-xs mt-2">生年月日に正しい日付を入力してください</p>
                {/if}
              </div>

              <div class="">
                <label for="telNo" class="block mt-4 form-input-label">{labelTel}</label>
                <input
                  type="text"
                  id="telNo"
                  name="telNo"
                  autocomplete="off"
                  class="block p-3 mt-1 appearance-none"
                  bind:value={$telNo.value}
                  required
                />
                {#if $myForm.hasError('telNo.required')}
                  <p class="text-red-500 text-xs mt-2">電話番号を入力してください</p>
                {/if}
              </div>

              <button type="button" class="py-3 mt-10 tracking-widest form-button" on:click={handleTemporaryLogin}
                >確認する</button
              >
            </div>
          </div>
        </form>
      </div>
    </div>

    <div class="grid place-items-center mt-10">
      <div class="content-center shadow-sm p-5 warning-background">
        <div class="grid place-items-start text-base">
          <div class="content-help-title">Gmailをご利用の方</div>
          <div class="content-help-text">メールが届かない場合は迷惑メールボックスをご確認ください。</div>
        </div>
        <div class="grid place-items-start text-base mt-5">
          <p class="content-help-title">そのほかのメールをご利用の方</p>
          <p class="content-help-text">
            メールが届かない場合は迷惑メールボックスをご確認ください。@unext.jpから送信したメールが迷惑メールと判断されてメールが届かないことがあります。受信できるように設定してください。
          </p>
        </div>
      </div>
    </div>
  </main>

  <footer class="container" />
</div>

<style lang="postcss">
  .login-background {
    background-color: white;
  }
  .content-help-title {
    color: #404040;
    font-size: 16px;
    font-weight: bold;
  }
  .content-help-text {
    color: #404040;
    font-size: 14px;
  }
  select {
    font-size: 14px;
    color: rgba(0, 10, 23, 1);
    background-color: rgba(1, 6, 13, 0.06);
    width: 100%;
    height: 48px;
    @apply focus:ring-0 focus:ring-blue-200 focus:border-transparent;
  }
  input[type='text'],
  input[type='email'],
  input[type='date'] {
    font-size: 14px;
    color: rgba(0, 10, 23, 1);
    background-color: rgba(1, 6, 13, 0.06);
    width: 100%;
    height: 48px;
    @apply focus:ring-0 focus:ring-blue-200 focus:border-transparent;
  }
  .alert-background {
    width: 720px;
  }
  @media screen and (max-width: 640px) {
    .alert-background {
      width: 100%;
    }
  }

  .form-background {
    width: 720px;
    background-color: rgba(0, 0, 0, 0.02);
  }
  @media screen and (max-width: 640px) {
    .form-background {
      width: 100%;
    }
  }
  .warning-background {
    width: 720px;
    background-color: rgba(0, 0, 0, 0.12);
  }
  @media screen and (max-width: 640px) {
    .warning-background {
      width: 100%;
    }
  }
  .form-input-label {
    font-size: 16px;
    color: rgba(0, 0, 0, 1);
  }
  .form-button {
    font-size: 14px;
    font-weight: bold;
    color: #ffffff;
    background-color: #0fb9f5;
    width: 100%;
    height: 56px;
  }
  .form-button:hover {
    text-decoration: none;
    -webkit-filter: brightness(1.1);
    filter: brightness(1.1);
  }
  .form-container {
    width: 352px;
  }
  @media screen and (max-width: 640px) {
    .form-container {
      width: 100%;
    }
  }
</style>
